import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, type ErrorComponentProps } from "@tanstack/react-router";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { ApiKeyList } from "@/features/api-access/api-key-list";
import { IssueKeyDialog } from "@/features/api-access/issue-key/dialog";
import { apiKeysQuery } from "@/features/api-access/queries";
import { StandingNotice } from "@/features/reseller/standing-notice";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

// /settings/api-keys: the reseller's v3 keys and, per key, its whitelist. Only an active
// reseller may change them; the buttons are a courtesy, the API's guard() is the gate
// (guide, rule 14), and a role that may not see keys gets the API's 403 as a page.
export const Route = createFileRoute("/_authenticated/settings/api-keys")({
  loader: ({ context }) => context.queryClient.ensureQueryData(apiKeysQuery()),
  staticData: { crumb: m.api_keys_nav },
  component: ApiKeysPage,
  errorComponent: ApiKeysError,
});

function ApiKeysPage() {
  const { reseller } = Route.useRouteContext();
  const { data: keys } = useSuspenseQuery(apiKeysQuery());
  const active = reseller.status === "active";
  return (
    <>
      {!active && <StandingNotice status={reseller.status} />}
      {/* The page's heading: shadcn's Item as shipped, its action a new key. */}
      <Item>
        <ItemContent>
          <ItemTitle>{m.api_keys_nav()}</ItemTitle>
          <ItemDescription>{m.api_keys_page_description()}</ItemDescription>
        </ItemContent>
        {active && (
          <ItemActions>
            <IssueKeyDialog />
          </ItemActions>
        )}
      </Item>
      <ApiKeyList keys={keys} canManage={active} />
    </>
  );
}

function ApiKeysError({ error }: ErrorComponentProps) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{forbidden ? m.api_keys_no_access_title() : m.common_error_title()}</EmptyTitle>
        <EmptyDescription>
          {forbidden ? m.api_keys_no_access_description() : m.common_error_description()}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
